/**
 * Durable token storage.
 *
 * The problem this solves: `getToken()` is called synchronously on every API
 * request, but Capacitor Preferences is async. Making the token read async
 * would turn every call site in the app into a promise chain for no benefit.
 *
 * So: localStorage stays the synchronous source of truth, and every write is
 * mirrored into Preferences (Android SharedPreferences), which survives a
 * WebView storage clear. At launch, `hydrateToken()` runs once before the app
 * renders — if localStorage is empty but Preferences has a token, it copies it
 * back. The user stays signed in through the cases that would otherwise log
 * them out for no visible reason.
 */

import { loadPlugin } from './platform'

const TOKEN_KEY = 'macro.token'

async function preferences() {
  return loadPlugin(() => import('@capacitor/preferences'), 'Preferences')
}

/** Mirror a token write to durable native storage. Fire-and-forget. */
export function persistToken(token: string | null): void {
  void (async () => {
    const prefs = await preferences()
    if (!prefs) return
    try {
      if (token === null) await prefs.remove({ key: TOKEN_KEY })
      else await prefs.set({ key: TOKEN_KEY, value: token })
    } catch (err) {
      console.warn('[native] could not persist token:', err)
    }
  })()
}

/**
 * Restore the token from native storage if the WebView lost it.
 * Call once, before rendering. Resolves to true if it recovered a session.
 */
export async function hydrateToken(): Promise<boolean> {
  const prefs = await preferences()
  if (!prefs) return false

  try {
    const existing = localStorage.getItem(TOKEN_KEY)
    if (existing) {
      // localStorage wins — it's the one the app just used. Make sure the
      // durable copy agrees, in case a previous mirror write failed.
      await prefs.set({ key: TOKEN_KEY, value: existing })
      return false
    }

    const { value } = await prefs.get({ key: TOKEN_KEY })
    if (value) {
      localStorage.setItem(TOKEN_KEY, value)
      return true
    }
  } catch (err) {
    console.warn('[native] token hydration failed:', err)
  }
  return false
}
